import React from "react";
import {CheckCircleFilled, CloseCircleFilled} from "@ant-design/icons";
import {Text, TextAs, TextSizes} from "@clockout-dev/design-elements";
import {MODES, REJECTION_REASON_LABELS} from "../uploads-simulator.constants";
import pillStyles from "../styles/pills.module.scss";
import styles from "../styles/results.module.scss";

function formatDuration(durationMs) {
    if (typeof durationMs !== "number" || !Number.isFinite(durationMs)) return null;
    return durationMs >= 1000 ? `${(durationMs / 1000).toFixed(1)}s` : `${durationMs}ms`;
}

/**
 * Top-of-report ruling: green accepted / red rejected, the INTERNAL reason
 * code with its human label (the member-visible wording lives in
 * `MemberVsInternalCard`), run duration and a mode pill. Tolerates a partial
 * `result` — missing keys degrade to em-dashes, never a crash.
 *
 * @param {Object} props
 * @param {Object} [props.result] — report `result`
 * @param {string} [props.mode]
 * @param {number} [props.durationMs]
 */
function RulingBanner({result, mode, durationMs}) {
    const accepted = result?.accepted === true;
    const reasonCode = result?.reason || null;
    const duration = formatDuration(durationMs);

    const facts = [
        result?.compensation_type ? `Compensation: ${result.compensation_type}` : null,
        typeof result?.batch_size === "number" ? `Batch: ${result.batch_size}` : null,
        typeof result?.contiguous_count === "number" && typeof result?.required_count === "number" ? `Contiguous: ${result.contiguous_count}/${result.required_count}` : null,
        typeof result?.shifts_persisted === "number" ? `Shifts persisted: ${result.shifts_persisted}` : null
    ].filter(Boolean);

    return (
        <div className={`${styles.rulingBanner} ${accepted ? styles.rulingAccepted : styles.rulingRejected}`}>
            <span className={styles.rulingIcon}>{accepted ? <CheckCircleFilled /> : <CloseCircleFilled />}</span>
            <div className={styles.rulingBody}>
                <div className={styles.rulingHeadline}>
                    <Text as={TextAs.SPAN} size={TextSizes.LARGE} className={styles.rulingVerdict}>
                        {accepted ? "Accepted" : "Rejected"}
                    </Text>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={`${pillStyles.tag} ${pillStyles.tone_soft}`}>
                        {MODES[mode]?.label || mode || "—"}
                    </Text>
                    {duration ? (
                        <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.rulingDuration}>
                            {duration}
                        </Text>
                    ) : null}
                </div>
                {reasonCode ? (
                    <Text as={TextAs.P} size={TextSizes.SMALL} className={styles.rulingReason}>
                        {REJECTION_REASON_LABELS[reasonCode] || reasonCode}
                        <span className={styles.rulingReasonCode}> · {reasonCode}</span>
                    </Text>
                ) : null}
                {facts.length ? (
                    <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.rulingFacts}>
                        {facts.join("  ·  ")}
                    </Text>
                ) : null}
            </div>
        </div>
    );
}

export default RulingBanner;
